import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Card from '../components/common/Card';
import LoadingSpinner from '../components/common/LoadingSpinner';
import BeneficiairesAPlanifier from '../components/beneficiaires/BeneficiairesAPlanifier';
import { Calendar, Clock, Users, AlertCircle, UserCheck, UserX, MapPin } from 'lucide-react';
import api from '../services/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [interventionsToday, setInterventionsToday] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heuresSemaine, setHeuresSemaine] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const interventionsRes = await api.get(`/interventions?date=${today}`);
      setInterventionsToday(interventionsRes.data);

      // Charger heures de la semaine pour auxiliaires
      if (user.role === 'auxiliaire') {
        const monday = new Date();
        monday.setDate(monday.getDate() - monday.getDay() + 1);
        const sunday = new Date(monday);
        sunday.setDate(sunday.getDate() + 6);

        const heuresRes = await api.get(
          `/heures/auxiliaire/${user.id}?debut=${format(monday, 'yyyy-MM-dd')}&fin=${format(sunday, 'yyyy-MM-dd')}`
        );
        setHeuresSemaine(heuresRes.data);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const getVigilanceColor = (niveau) => {
    const colors = {
      vert: 'bg-emerald-500',
      orange: 'bg-orange-500',
      rouge: 'bg-red-500'
    };
    return colors[niveau] || 'bg-gray-400';
  };

  const getVigilanceBadge = (niveau) => {
    const badges = {
      vert: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Normal' },
      orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', label: 'Attention' },
      rouge: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Urgent' }
    };
    return badges[niveau] || { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', label: 'N/A' };
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="pb-20 bg-gradient-to-br from-blue-50 via-white to-purple-50 min-h-screen">
      {/* Header avec gradient moderne */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white p-6 rounded-b-[2rem] shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-1 drop-shadow-sm">
              👋 Bonjour {user.prenom}
            </h1>
            <p className="text-blue-100 text-sm font-medium">
              {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
            </p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
            <Calendar size={28} className="text-white" />
          </div>
        </div>
      </div>

      <div className="p-4 space-y-5">
        {/* Widget heures pour auxiliaires - Design moderne */}
        {user.role === 'auxiliaire' && heuresSemaine && (
          <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-lg border border-blue-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-blue-500 rounded-xl p-2">
                <Clock size={20} className="text-white" />
              </div>
              <h2 className="text-lg font-bold text-gray-800">
                Heures cette semaine
              </h2>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-blue-100">
                <p className="text-2xl font-bold text-blue-600">{heuresSemaine.heuresSemaine}h</p>
                <p className="text-xs text-gray-600 mt-1 font-medium">Semaine</p>
              </div>
              <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-orange-100">
                <p className="text-2xl font-bold text-orange-600">{heuresSemaine.heuresWeekend}h</p>
                <p className="text-xs text-gray-600 mt-1 font-medium">Week-end</p>
              </div>
              <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-green-100">
                <p className="text-2xl font-bold text-green-600">{heuresSemaine.total}h</p>
                <p className="text-xs text-gray-600 mt-1 font-medium">Total</p>
              </div>
            </div>
          </div>
        )}

        {/* Statistiques pour coordinatrices - Design amélioré */}
        {user.role === 'coordinatrice' && (
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg p-5 text-white">
              <div className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center mb-3">
                <Calendar size={24} className="text-white" />
              </div>
              <p className="text-3xl font-bold mb-1">{interventionsToday.length}</p>
              <p className="text-sm text-blue-100 font-medium">Interventions aujourd'hui</p>
            </div>
            <div 
              className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-lg p-5 text-white cursor-pointer transform hover:scale-105 transition-transform active:scale-95"
              onClick={() => navigate('/gestion')}
            >
              <div className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center mb-3">
                <Users size={24} className="text-white" />
              </div>
              <p className="text-2xl font-bold mb-1">Gestion</p>
              <p className="text-sm text-green-100 font-medium">Accéder au panneau →</p>
            </div>
          </div>
        )}

        {/* Bénéficiaires à planifier - Coordinatrices uniquement */}
        {user.role === 'coordinatrice' && (
          <BeneficiairesAPlanifier />
        )}

        {/* Interventions du jour - Design ultra-moderne */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl p-2">
              <Calendar size={20} className="text-white" />
            </div>
            <h2 className="text-lg font-bold text-gray-800">
              Interventions d'aujourd'hui
              <span className="ml-2 text-sm font-normal text-gray-500">({interventionsToday.length})</span>
            </h2>
          </div>

          {interventionsToday.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 text-center">
              <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Calendar size={40} className="text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">Aucune intervention prévue</p>
              <p className="text-sm text-gray-400 mt-1">Profitez de cette journée calme 😊</p>
            </div>
          ) : (
            <div className="space-y-4">
              {interventionsToday.map((intervention) => {
                const vigilanceBadge = getVigilanceBadge(intervention.beneficiaireId?.niveauVigilance);
                
                return (
                  <div
                    key={intervention._id}
                    onClick={() => navigate(`/intervention/${intervention._id}`)}
                    className="bg-white rounded-2xl shadow-md hover:shadow-xl border border-gray-100 p-5 cursor-pointer transform hover:scale-[1.02] transition-all duration-200"
                  >
                    {/* En-tête avec vigilance et horaires */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-3 flex-1">
                        {/* Indicateur vigilance */}
                        <div className={`w-1 h-full rounded-full ${getVigilanceColor(intervention.beneficiaireId?.niveauVigilance)}`}></div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-lg text-gray-800">
                              {intervention.beneficiaireId?.prenom} {intervention.beneficiaireId?.nom}
                            </h3>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${vigilanceBadge.bg} ${vigilanceBadge.text} border ${vigilanceBadge.border}`}>
                              {vigilanceBadge.label}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 font-medium">{intervention.type}</p>
                        </div>
                      </div>

                      {/* Horaires avec design moderne */}
                      <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl px-3 py-2 text-center border border-blue-100 ml-3">
                        <p className="font-bold text-blue-700 text-sm">{intervention.heureDebut}</p>
                        <p className="text-xs text-gray-500">à</p>
                        <p className="font-bold text-purple-700 text-sm">{intervention.heureFin}</p>
                      </div>
                    </div>

                    {/* Auxiliaire attribué (coordinatrices uniquement) */}
                    {user.role === 'coordinatrice' && (
                      <div className="mb-3">
                        {intervention.user ? (
                          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl px-3 py-2">
                            <div className="bg-green-500 rounded-full p-1">
                              <UserCheck size={14} className="text-white" />
                            </div>
                            <span className="text-green-800 font-bold text-sm">
                              {intervention.user.username}
                            </span>
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-300 rounded-xl px-3 py-2 animate-pulse">
                            <div className="bg-red-500 rounded-full p-1">
                              <UserX size={14} className="text-white" />
                            </div>
                            <span className="text-red-800 font-bold text-sm">
                              ⚠️ Aucun auxiliaire
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Adresse */}
                    <div className="flex items-start gap-2 text-sm text-gray-600 mb-3 bg-gray-50 rounded-lg p-2">
                      <MapPin size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <span className="font-medium">
                        {intervention.beneficiaireId?.adresse?.rue}, {intervention.beneficiaireId?.adresse?.ville}
                      </span>
                    </div>

                    {/* Consignes */}
                    {intervention.beneficiaireId?.consignes && (
                      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-400 rounded-lg p-3 flex gap-3">
                        <div className="bg-amber-400 rounded-full p-1 h-fit">
                          <AlertCircle size={16} className="text-white" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-amber-900 mb-1">⚠️ Consignes importantes</p>
                          <p className="text-xs text-amber-800 leading-relaxed">{intervention.beneficiaireId.consignes}</p>
                        </div>
                      </div>
                    )}

                    {/* Statut effectuée */}
                    {intervention.statut === 'effectuee' && (
                      <div className="mt-3 inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg">
                        <span>✓</span>
                        <span>Intervention effectuée</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
